"use client"

import React, { useState, useEffect, useRef, useCallback } from "react"
import Link from "next/link"
import { Bell, CheckCheck, ExternalLink, Loader2 } from "lucide-react"
import { Button } from "../ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover"
import { ScrollArea } from "../ui/scroll-area"
import { toast } from "sonner"
import { cn, getSharedCookie, setSharedCookie } from "../lib/utils"
import { authFetch, getAccountApiUrl, updateUserPreferences, useSidebarUser, isAuthenticated } from "../lib/auth"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip"

export interface NotificationItem {
  id: string
  userId?: string
  scope?: string
  title: string
  message: string
  metadata?: Record<string, unknown>
  readAt: string | null
  createdAt: string
}

export interface NotificationBellProps {
  /** Returns current auth token; if not provided or returns null, bell is hidden */
  getAuthToken?: () => string | null
  /** API base URL for notifications (default: process.env.NEXT_PUBLIC_API_URL) */
  apiBaseUrl?: string
  /** Link for "View all" (e.g. "/notifications") */
  notificationsHref?: string
  /** Poll interval in ms (default 45000) */
  pollIntervalMs?: number
  /** Client-side notifications merged in ahead of the API ones (e.g. finance
   *  triggered price alerts). Shown + counted; read-state managed by the source app. */
  localItems?: NotificationItem[]
}

const DEFAULT_POLL_MS = 45000
const FETCH_LIMIT = 10

function useNotifications(
  getAuthToken: (() => string | null) | undefined,
  apiBaseUrl: string,
  enabled: boolean,
  preferences?: any
) {
  const [items, setItems] = useState<NotificationItem[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [markingRead, setMarkingRead] = useState(false)
  const seenIdsRef = useRef<Set<string>>(new Set())
  const hasFetchedOnceRef = useRef(false)
  const isMountedRef = useRef(true)
  // Stop polling once the account backend says we're not authorised (stale cross-subdomain
  // session marker): otherwise the bell floods the console with /notifications 401s every poll.
  const authFailedRef = useRef(false)

  const base = (apiBaseUrl ?? "").replace(/\/$/, "")

  // 1. Initialize seen IDs from shared cookie OR backend preferences
  useEffect(() => {
    const prefIds = preferences?.notifications?.seenIds
    if (Array.isArray(prefIds)) {
      seenIdsRef.current = new Set(prefIds)
      return
    }

    const stored = getSharedCookie("AskFinz_seen_notifications")
    if (stored) {
      try {
        const ids = JSON.parse(stored)
        if (Array.isArray(ids)) seenIdsRef.current = new Set(ids)
      } catch { }
    }
  }, [preferences?.notifications?.seenIds])

  const fetchList = useCallback(async () => {
    if (!enabled || authFailedRef.current) return
    setLoading(true)
    try {
      const url = `${getAccountApiUrl()}/api/account/notifications?limit=${FETCH_LIMIT}`
      const res = await authFetch(url, { skipAuthRedirect: true })
      if (res.status === 401) { authFailedRef.current = true; return }
      if (!res.ok || !isMountedRef.current) return
      const data = await res.json()
      const list: NotificationItem[] = data.items ?? []
      setItems(list)

      if (hasFetchedOnceRef.current) {
        let hasNewSeen = false
        list.forEach((n) => {
          if (!n.readAt && !seenIdsRef.current.has(n.id)) {
            toast.info(n.title, { description: n.message })
            seenIdsRef.current.add(n.id)
            hasNewSeen = true
          }
        })

        if (hasNewSeen) {
           syncSeenIds(Array.from(seenIdsRef.current))
        }
      } else {
        list.forEach((n) => seenIdsRef.current.add(n.id))
        hasFetchedOnceRef.current = true
        syncSeenIds(Array.from(seenIdsRef.current))
      }
    } catch {
      // Account API unreachable/blocked (cross-subdomain CORS or offline) — stop polling
      // so the bell doesn't spam the console; it retries on the next mount/navigation.
      authFailedRef.current = true
      if (isMountedRef.current) setItems([])
    } finally {
      if (isMountedRef.current) setLoading(false)
    }
  }, [enabled])

  const syncSeenIds = useCallback((ids: string[]) => {
    const limited = ids.slice(-100)
    setSharedCookie("AskFinz_seen_notifications", JSON.stringify(limited))
    updateUserPreferences({ 
        notifications: { seenIds: limited } 
    })
  }, [])

  const fetchUnreadCount = useCallback(async () => {
    if (!enabled || authFailedRef.current) return
    try {
      const url = `${getAccountApiUrl()}/api/account/notifications/unread-count`
      const res = await authFetch(url, { skipAuthRedirect: true })
      if (res.status === 401) { authFailedRef.current = true; return }
      if (!res.ok || !isMountedRef.current) return
      const data = await res.json()
      setUnreadCount(data.count ?? 0)
    } catch {
      authFailedRef.current = true
      if (isMountedRef.current) setUnreadCount(0)
    }
  }, [enabled])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    // SECURITY V-029: poll whenever the session is active (cookie-based). Do
    // NOT require a JS-readable token — the JWT is HttpOnly now, so
    // getAuthToken() returns null and authFetch carries the cookie instead.
    if (!enabled) return
    fetchUnreadCount()
    fetchList()
    const t = setInterval(() => {
      fetchUnreadCount()
      fetchList()
    }, DEFAULT_POLL_MS)
    return () => clearInterval(t)
  }, [enabled, fetchUnreadCount, fetchList])

  const markAllRead = useCallback(async () => {
    if (!enabled) return
    setMarkingRead(true)
    try {
      const url = `${getAccountApiUrl()}/api/account/notifications/mark-all-read`
      const res = await authFetch(url, { method: "POST", skipAuthRedirect: true })
      if (res.ok && isMountedRef.current) {
        setUnreadCount(0)
        setItems((prev) =>
          prev.map((n) => ({ ...n, readAt: n.readAt ?? new Date().toISOString() }))
        )
      }
    } finally {
      if (isMountedRef.current) setMarkingRead(false)
    }
  }, [enabled])

  return {
    items,
    unreadCount,
    loading,
    markingRead,
    refetch: fetchList,
    refetchCount: fetchUnreadCount,
    markAllRead,
  }
}


export function NotificationBell({
  getAuthToken,
  apiBaseUrl = typeof process !== "undefined" ? process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000" : "http://localhost:8000",
  notificationsHref,
  pollIntervalMs = DEFAULT_POLL_MS,
  localItems = [],
}: NotificationBellProps) {
  const [open, setOpen] = useState(false)
  const user = useSidebarUser()
  // SECURITY V-029 / hydration: the JWT is HttpOnly, so we gate visibility on
  // the cross-subdomain session marker (isAuthenticated) rather than a
  // JS-readable token. isClient defers the check to after mount so the server
  // and first client render agree (both null) — no React #418.
  const [isClient, setIsClient] = useState(false)
  useEffect(() => { setIsClient(true) }, [])
  const enabled = isClient && isAuthenticated()

  const {
    items: apiItems,
    unreadCount: apiUnread,
    loading,
    markingRead,
    refetch,
    refetchCount,
    markAllRead,
  } = useNotifications(getAuthToken, apiBaseUrl, enabled, user?.preferences)

  // Merge client-side items (e.g. finance price alerts) ahead of API items.
  const items = localItems.length ? [...localItems, ...apiItems] : apiItems
  const unreadCount = apiUnread + localItems.filter((n) => !n.readAt).length

  // Show the bell if authenticated OR there are local items to surface.
  if (!enabled && localItems.length === 0) return null

  const handleOpen = (isOpen: boolean) => {
    setOpen(isOpen)
    if (isOpen) {
      refetch()
      refetchCount()
    }
  }

  return (
    <TooltipProvider>
      <Popover open={open} onOpenChange={handleOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-full relative"
                aria-label="Notifications"
              >
                <Bell className="h-4 w-4" />
                {unreadCount > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-medium px-1"
                    style={{ backgroundColor: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))" }}
                  >
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">Notifications</TooltipContent>
        </Tooltip>
        <PopoverContent className="w-[320px] p-0 mt-3" align="end" side="bottom" sideOffset={12}>
          <div className="border-b bg-muted/30 px-3 py-2 flex items-center justify-between">
            <span className="font-medium text-sm">Notifications</span>
            <div className="flex items-center gap-1">
              {items.some((n) => !n.readAt) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={markAllRead}
                  disabled={markingRead}
                >
                  {markingRead ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <>
                      <CheckCheck className="h-3 w-3 mr-1" />
                      Mark all read
                    </>
                  )}
                </Button>
              )}
              {notificationsHref && (
                <Button variant="ghost" size="sm" className="h-7 text-xs" asChild>
                  <Link href={notificationsHref} onClick={() => setOpen(false)}>
                    <ExternalLink className="h-3 w-3 mr-1" />
                    View all
                  </Link>
                </Button>
              )}
            </div>
          </div>
          <ScrollArea className="max-h-[280px]">
            {loading && items.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground text-sm">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : items.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No notifications yet
              </div>
            ) : (
              <ul className="divide-y">
                {items.map((n) => (
                  <li
                    key={n.id}
                    className={cn(
                      "px-3 py-2.5 text-left hover:bg-muted/50 transition-colors",
                      !n.readAt && "bg-muted/20"
                    )}
                  >
                    <div className="font-medium text-sm">{n.title}</div>
                    <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                      {n.message}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">
                      {n.createdAt
                        ? new Date(n.createdAt).toLocaleString(undefined, {
                          dateStyle: "short",
                          timeStyle: "short",
                        })
                        : ""}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  )
}
